import React, { useCallback, useEffect, useRef, useState } from 'react';
import api, { openSafeTelegramLink, isSafeTelegramUrl } from '../api/client';
import { CoinIcon, FloatIcon, RubyIcon, ScreenTitle, StarIcon } from './CurrencyIcons';
import AlertModal from './AlertModal';
import PassStickerBurst from './PassStickerBurst';
import { iconUrl } from '../utils/assets';
import { useLocale } from '../i18n/LocaleContext';
import { useGameAlert } from '../hooks/useGameAlert';
import { celebrate } from '../utils/fx';
import { addToHomeScreen, checkHomeScreenStatus, requestHomeScreen, canAddToHomeScreen, hasHomeScreenApi } from '../utils/telegram';
import { mergeClickerState } from '../utils/clickerMerge';
import { awaitDrainPendingTaps } from '../utils/tapFlush';
import { payWithStars, prefetchStarsInvoice, watchPaymentSettle } from '../utils/starsPay';

const formatLeft = (sec) => {
  if (sec == null) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  return `${h}h ${m}m`;
};

const Tasks = ({ onClickerUpdate }) => {
  const { t, lang } = useLocale();
  const { alert, closeAlert, confirmAlert, showError, showInfo, showConfirm } = useGameAlert();
  const [items, setItems] = useState([]);
  const [bp, setBp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [bpBusy, setBpBusy] = useState(false);
  const [showHow, setShowHow] = useState(false);
  const [sticker, setSticker] = useState(false);
  const verifyCleanupRef = useRef(null);

  useEffect(() => () => {
    verifyCleanupRef.current?.();
  }, []);

  const mergeClicker = useCallback((synced) => {
    if (!synced) return;
    onClickerUpdate?.((prev) => mergeClickerState(prev, synced, { preferRemoteBalance: true }));
  }, [onClickerUpdate]);

  const flushThenMerge = useCallback(async () => {
    const drained = await awaitDrainPendingTaps();
    if (drained.last) mergeClicker(drained.last);
  }, [mergeClicker]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tasks, pass] = await Promise.all([api.tasksList(), api.battlePass()]);
      setItems(tasks.items || []);
      setBp(pass);

      // Переход на биржу уводит из мини-приложения, и вернуться человек может
      // уже отдельным запуском — тогда проверка «по возвращении» не сработает.
      // Поэтому при открытии экрана один раз тихо спрашиваем: не заходил ли он
      // туда с прошлого раза. Ошибку глотаем: это фон, а не действие игрока.
      const pending = (tasks.items || []).find(
        (task) => task.task_type === 'visit_freelance' && task.status === 'open'
      );
      if (pending) {
        api
          .tasksCheck(pending.id)
          .then((result) => result?.tasks && setItems(result.tasks))
          .catch(() => {});
      }
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  const claim = async (id) => {
    setBusyId(id);
    try {
      await flushThenMerge();
      const result = await api.tasksClaim(id);
      setItems(result.tasks || []);
      if (result.clicker) mergeClicker(result.clicker);
      setBp(await api.battlePass());
      try {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } catch (_) {
        /* ignore */
      }
    } catch (err) {
      showError(err);
    } finally {
      setBusyId(null);
    }
  };

  const checkSubscribe = (task) => {
    const url = task.meta?.url;
    if (url && isSafeTelegramUrl(url)) openSafeTelegramLink(url);

    const runCheck = () => {
      setBusyId(task.id);
      api
        .tasksCheck(task.id)
        .then((result) => setItems(result.tasks || []))
        .catch((err) => showError(err))
        .finally(() => setBusyId(null));
    };

    verifyCleanupRef.current?.();
    let checked = false;
    let timeoutId = null;
    const tryCheck = () => {
      if (checked) return;
      checked = true;
      cleanup();
      setTimeout(runCheck, 700);
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') tryCheck();
    };
    const onFocus = () => tryCheck();
    const cleanup = () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      if (timeoutId) clearTimeout(timeoutId);
      if (verifyCleanupRef.current === cleanup) verifyCleanupRef.current = null;
    };
    verifyCleanupRef.current = cleanup;
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    timeoutId = setTimeout(() => {
      if (!checked) tryCheck();
    }, 10000);
  };

  const finishHomeQuest = async (task, homeStatus) => {
    const result = await api.tasksCheck(task.id, { home_status: homeStatus });
    setItems(result.tasks || []);
    if (result.clicker) mergeClicker(result.clicker);
    // Check only marks done — player claims reward via claim button
    showInfo({
      icon: 'home.png',
      title: t('homeAddedOk') || (lang === 'en' ? 'Added! Claim your reward.' : 'Добавлено! Заберите награду.'),
      confirmLabel: t('gotIt'),
    });
  };

  const checkHomeScreen = (task) => {
    // Must fire in the same tap. Bypass JS version gate via postEvent.
    if (!canAddToHomeScreen()) {
      showInfo({ icon: 'bulb.png', title: t('homeUnsupported'), confirmLabel: t('gotIt') });
      return;
    }

    const prompted = addToHomeScreen();

    setBusyId(task.id);
    (async () => {
      try {
        if (prompted || hasHomeScreenApi()) {
          let status = await checkHomeScreenStatus(1200);
          if (status === 'added') {
            await finishHomeQuest(task, 'added');
            return;
          }
          // Second nudge + wait for homeScreenAdded
          status = await requestHomeScreen(7000);
          if (status === 'added') {
            await finishHomeQuest(task, 'added');
            return;
          }
          status = await checkHomeScreenStatus(800);
          if (status === 'added') {
            await finishHomeQuest(task, 'added');
            return;
          }

          // Native sheet may have shown without status events — confirm after user acts
          const ok = await showConfirm({
            icon: 'home.png',
            title: t('homeAdd'),
            body:
              t('homeConfirmBody') ||
              (lang === 'en'
                ? 'Did the shortcut dialog appear? Add Tap Mars, then tap Yes.'
                : 'Появилось окно ярлыка? Добавь Tap Mars, затем нажми «Да».'),
            confirmLabel: t('homeConfirmYes'),
            cancelLabel: t('close'),
          });
          if (!ok) return;
          // One more native nudge in case the first was swallowed
          addToHomeScreen();
          const again = await checkHomeScreenStatus(1000);
          await finishHomeQuest(task, again === 'added' ? 'added' : 'confirmed');
          return;
        }

        showInfo({
          icon: 'bulb.png',
          title: t('homeUnsupported'),
          body:
            t('homeNeedOfficial') ||
            (lang === 'en'
              ? 'Home shortcut API is missing. Open official Telegram → menu ⋯ → Add to Home Screen.'
              : 'Не удалось вызвать API ярлыка. Открой официальный Telegram → меню ⋯ → «Добавить на главный экран».'),
          confirmLabel: t('gotIt'),
        });
      } catch (err) {
        showError(err);
      } finally {
        setBusyId(null);
      }
    })();
  };

  const checkBoost = (task) => {
    // One tap: open boost, auto-verify when the mini-app is focused again.
    const url = task.meta?.url || 'https://t.me/boost/Tap_Mars';
    if (isSafeTelegramUrl(url)) openSafeTelegramLink(url);

    const runCheck = () => {
      setBusyId(task.id);
      api
        .tasksCheck(task.id)
        .then((result) => {
          setItems(result.tasks || []);
        })
        .catch((err) => showError(err))
        .finally(() => setBusyId(null));
    };

    verifyCleanupRef.current?.();
    let checked = false;
    let timeoutId = null;
    const tryCheck = () => {
      if (checked) return;
      checked = true;
      cleanup();
      setTimeout(runCheck, 900);
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') tryCheck();
    };
    const onFocus = () => tryCheck();
    const cleanup = () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      if (timeoutId) clearTimeout(timeoutId);
      if (verifyCleanupRef.current === cleanup) verifyCleanupRef.current = null;
    };
    verifyCleanupRef.current = cleanup;
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    timeoutId = setTimeout(() => {
      if (!checked) tryCheck();
    }, 12000);
  };

  /**
   * Переход на биржу фриланса.
   *
   * Ссылка открывается в том же касании — после `await` часть клиентов Telegram
   * считает переход не пользовательским и молча его глотает. Проверка идёт
   * следом, по возвращении: биржа заводит учётную запись только при настоящем
   * открытии мини-приложения, поэтому её наличие и есть доказательство визита.
   */
  const checkFreelance = (task) => {
    const url = task.meta?.url || 'https://t.me/Tap_Mars_Freelance_bot';
    if (isSafeTelegramUrl(url)) openSafeTelegramLink(url);

    const runCheck = () => {
      setBusyId(task.id);
      api
        .tasksCheck(task.id)
        .then((result) => setItems(result.tasks || []))
        .catch(() => {
          // Молчим: человек мог просто заглянуть в чат бота и не открыть
          // приложение. Ругаться на это нечестно — задание просто не закрылось.
        })
        .finally(() => setBusyId(null));
    };

    verifyCleanupRef.current?.();
    let checked = false;
    let timeoutId = null;
    const tryCheck = () => {
      if (checked) return;
      checked = true;
      cleanup();
      setTimeout(runCheck, 900);
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') tryCheck();
    };
    const onFocus = () => tryCheck();
    const cleanup = () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      if (timeoutId) clearTimeout(timeoutId);
      if (verifyCleanupRef.current === cleanup) verifyCleanupRef.current = null;
    };
    verifyCleanupRef.current = cleanup;
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    timeoutId = setTimeout(() => {
      if (!checked) tryCheck();
    }, 20000);
  };

  const runStarsPay = (sku, onPaid) => {
    if (bpBusy) return;
    setBpBusy(true);
    const onStatus = async (status) => {
      if (status === 'cancelled' || status === 'failed') {
        setBpBusy(false);
        return;
      }
      if (status === 'paid') {
        try {
          await onPaid?.();
        } finally {
          setBpBusy(false);
          verifyCleanupRef.current?.();
          verifyCleanupRef.current = null;
        }
      }
    };

    verifyCleanupRef.current?.();
    let keepBusy = false;
    payWithStars({ sku, onStatus })
      .then((result) => {
        if (result.reason === 'error') {
          showError(result.error);
          return;
        }
        if (result.chat_sent || result.needsPoll) {
          keepBusy = true;
          showInfo({
            icon: 'crown.png',
            title: t('starsLabel') || 'Stars',
            body:
              t('starsSentChat') ||
              (lang === 'en'
                ? 'Invoice sent to the bot chat — open @Tap_Mars_bot and pay there.'
                : 'Счёт отправлен в чат с ботом — открой @Tap_Mars_bot и оплати там.'),
            confirmLabel: t('gotIt'),
          });
          const stop = watchPaymentSettle(async () => {
            setBp(await api.battlePass());
            mergeClicker(await api.clickerSync());
          });
          verifyCleanupRef.current = () => {
            stop();
            setBpBusy(false);
          };
          setTimeout(() => verifyCleanupRef.current?.(), 45000);
          return;
        }
        if (result.needsRetap || result.reason === 'needs_retap') {
          showInfo({
            icon: 'crown.png',
            title: t('starsLabel') || 'Stars',
            body: t('starsTapAgain') || (lang === 'en' ? 'Tap again to open payment.' : 'Нажми ещё раз, чтобы открыть оплату.'),
            confirmLabel: t('gotIt'),
          });
          return;
        }
        if (!result.ok) {
          showInfo({ title: t('starsOnly'), icon: 'bulb.png' });
        }
      })
      .catch((err) => showError(err))
      .finally(() => {
        if (!keepBusy) setBpBusy(false);
      });
  };

  const buyPass = (sku = 'battle_pass_premium') => {
    runStarsPay(sku, async () => {
      setBp(await api.battlePass());
      mergeClicker(await api.clickerSync());
      showInfo({ icon: 'crown.png', title: t('premiumBought'), confirmLabel: t('gotIt') });
    });
  };

  const claimBp = async (level, track) => {
    if (bpBusy) return;
    setBpBusy(true);
    try {
      await flushThenMerge();
      const prevLevel = bp?.level || 0;
      const result = await api.battlePassClaim(level, track);
      setBp(result);
      if ((result.level || 0) > prevLevel || level > prevLevel) {
        celebrate('level');
        setSticker(true);
      }
      if (result.clicker) mergeClicker(result.clicker);
      else mergeClicker(await api.clickerSync());
    } catch (err) {
      showError(err);
    } finally {
      setBpBusy(false);
    }
  };

  const claimAll = async () => {
    if (bpBusy) return;
    setBpBusy(true);
    try {
      await flushThenMerge();
      const prevLevel = bp?.level || 0;
      const result = await api.battlePassClaimAll();
      setBp(result);
      if ((result.level || 0) > prevLevel) {
        celebrate('level');
        setSticker(true);
      }
      if (result.clicker) mergeClicker(result.clicker);
      else mergeClicker(await api.clickerSync());
    } catch (err) {
      showError(err);
    } finally {
      setBpBusy(false);
    }
  };

  const claimDaily = async () => {
    if (bpBusy) return;
    setBpBusy(true);
    try {
      await flushThenMerge();
      const result = await api.battlePassDaily();
      setBp(result);
      if (result.clicker) mergeClicker(result.clicker);
      showInfo({
        icon: 'crown.png',
        title: `+${result.granted?.xp || 50} XP`,
        body: `+${result.granted?.coins || 15} ${t('coins')}`,
        confirmLabel: t('gotIt'),
      });
    } catch (err) {
      showError(err);
    } finally {
      setBpBusy(false);
    }
  };

  const xpPct = bp && bp.xp_per_level ? Math.min(100, Math.round((bp.xp / bp.xp_per_level) * 100)) : 0;
  const rewardFor = (lvl, track) =>
    (bp?.rewards || []).find((r) => r.level === lvl && r.track === track);

  const claimable =
    (bp?.claimable_free?.length || 0) + (bp?.claimable_premium?.length || 0);

  return (
    <main className="tasks-screen screen-enter">
      <ScreenTitle
        icon={iconUrl('crown.png')}
        title={t('passTitle')}
        subtitle={t('passSubtitle')}
      >
        <button type="button" className="how-btn" onClick={() => setShowHow(true)}>
          <FloatIcon src={iconUrl('bulb.png')} size={18} eager animated native />
          {t('passHowTitle')}
        </button>
      </ScreenTitle>

      {loading ? <div className="inline-message shimmer-line">{t('loading')}</div> : null}

      {bp ? (
        <div className="invite-panel bp-panel">
          <div className="bp-head">
            <div>
              <div className="upgrades-item-info-name">{bp.title}</div>
              <div className="item-desc">
                {t('passLevel')} {bp.level}/{bp.max_level} · {bp.premium ? t('passVip') : t('passFree')} ·{' '}
                {t('passEnds')} {formatLeft(bp.seconds_left)}
              </div>
            </div>
            {!bp.premium ? (
              <div className="bp-buy-col">
                <button
                  type="button"
                  className="action-btn stars"
                  disabled={bpBusy}
                  onPointerDown={() => prefetchStarsInvoice('battle_pass_premium_sub')}
                  onClick={() => buyPass('battle_pass_premium_sub')}
                >
                  <StarIcon size={16} />
                  {t('passSub')} · 99
                </button>
                <button
                  type="button"
                  className="action-btn"
                  disabled={bpBusy}
                  onPointerDown={() => prefetchStarsInvoice('battle_pass_premium')}
                  onClick={() => buyPass('battle_pass_premium')}
                >
                  <StarIcon size={16} />
                  {t('passVip')} · {bp.stars_price}
                </button>
              </div>
            ) : (
              <FloatIcon src={iconUrl('crown.png')} size={36} eager native />
            )}
          </div>

          <div className="bp-xp-bar" aria-hidden>
            <div className="bp-xp-fill" style={{ transform: `scaleX(${xpPct / 100})` }} />
          </div>
          <div className="item-desc bp-xp-label">
            {t('passXp')} {bp.xp}/{bp.xp_per_level} · {xpPct}%
          </div>

          <div className={`bp-actions ${!bp.daily_orbit_ready ? 'bp-actions-stacked' : ''}`}>
            <button
              type="button"
              className="action-btn soft-btn"
              disabled={bpBusy || !bp.daily_orbit_ready}
              onClick={claimDaily}
            >
              {bp.daily_orbit_ready ? t('passDailyClaim') : t('passDailyDone')}
            </button>
            {claimable > 0 ? (
              <button
                type="button"
                className="action-btn stars soft-btn"
                disabled={bpBusy}
                onClick={claimAll}
              >
                {t('passClaimAll')} ({claimable})
              </button>
            ) : null}
          </div>

          <div className="bp-currency-legend">
            <span className="reward-line">
              <CoinIcon size={14} /> {t('coins')}
            </span>
            <span className="reward-line">
              <RubyIcon size={14} /> {t('rubies')} — {t('passRubiesHint')}
            </span>
          </div>

          <div className="bp-levels">
            {Array.from({ length: bp.max_level }, (_, i) => i + 1).map((lvl) => {
              const freeClaimed = (bp.claimed_free || []).includes(lvl);
              const premClaimed = (bp.claimed_premium || []).includes(lvl);
              const unlocked = lvl <= bp.level;
              const free = rewardFor(lvl, 'free');
              const prem = rewardFor(lvl, 'premium');
              const milestone = lvl % 5 === 0;
              const renderReward = (r, claimed) => {
                if (claimed) return '✓';
                if (!r) return '—';
                if (r.currency === 'coins' || r.type === 'coins') {
                  return (
                    <span className="reward-line">
                      <CoinIcon size={12} /> +{r.amount ?? r.label}
                    </span>
                  );
                }
                if (r.currency === 'rubies' || r.type === 'amber') {
                  return (
                    <span className="reward-line">
                      <RubyIcon size={12} /> +{r.amount ?? r.label}
                    </span>
                  );
                }
                if (r.type === 'cosmetic' || r.code) {
                  return `🖼️ ${r.code || r.label}`;
                }
                return r.label || '—';
              };
              return (
                <div
                  key={lvl}
                  className={`bp-level-row ${unlocked ? 'is-open' : ''} ${milestone ? 'is-milestone' : ''}`}
                >
                  <span className="bp-lvl">
                    {t('passLevel')}
                    {lvl}
                  </span>
                  <button
                    type="button"
                    className="action-btn bp-reward-btn"
                    disabled={bpBusy || !unlocked || freeClaimed}
                    onClick={() => claimBp(lvl, 'free')}
                  >
                    {renderReward(free, freeClaimed)}
                  </button>
                  <button
                    type="button"
                    className="action-btn stars bp-reward-btn"
                    disabled={bpBusy || !bp.premium || !unlocked || premClaimed}
                    onClick={() => claimBp(lvl, 'premium')}
                  >
                    {renderReward(prem, premClaimed)}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="section-label">{t('tasksSection')}</div>
      <div className="upgrades-list">
        {items.map((task) => {
          const canClaim =
            task.status === 'done' || (task.progress >= task.target && task.status !== 'claimed');
          const claimed = task.status === 'claimed';
          return (
            <div className="upgrades-item" key={task.id}>
              <div className="item-text">
                <span className="upgrades-item-info-name">
                  {task.title}
                  {task.is_daily ? <span className="level-badge">{t('daily')}</span> : null}
                </span>
                <span className="item-desc">{task.description}</span>
                <span className="item-desc reward-line">
                  {t('reward')}: <CoinIcon size={14} /> {task.reward_coins}
                  {task.reward_amber ? (
                    <>
                      {' '}
                      · <RubyIcon size={14} /> {task.reward_amber}
                    </>
                  ) : null}
                  {task.reward_energy ? <> · ⚡{task.reward_energy}</> : null}
                  {task.reward_bp_xp ? (
                    <>
                      {' '}
                      · +{task.reward_bp_xp} {t('passXp')}
                    </>
                  ) : null}
                </span>
              </div>
              {claimed ? (
                <span className="btn-disabled">{t('done')}</span>
              ) : canClaim ? (
                <button
                  type="button"
                  className="action-btn"
                  disabled={busyId === task.id}
                  onClick={() => claim(task.id)}
                >
                  {t('claim')}
                </button>
              ) : task.task_type === 'subscribe' ? (
                <button
                  type="button"
                  className="action-btn stars"
                  disabled={busyId === task.id}
                  onClick={() => checkSubscribe(task)}
                >
                  {t('open')}
                </button>
              ) : task.task_type === 'home_screen' ? (
                <button
                  type="button"
                  className="action-btn stars"
                  disabled={busyId === task.id}
                  onClick={() => checkHomeScreen(task)}
                >
                  {t('homeAdd')}
                </button>
              ) : task.task_type === 'channel_boost' ? (
                <button
                  type="button"
                  className="action-btn stars"
                  disabled={busyId === task.id}
                  onClick={() => checkBoost(task)}
                >
                  {t('boostChannel')}
                </button>
              ) : task.task_type === 'visit_freelance' ? (
                <button
                  type="button"
                  className="action-btn stars"
                  disabled={busyId === task.id}
                  onClick={() => checkFreelance(task)}
                >
                  {t('visitFreelance')}
                </button>
              ) : (
                <span className="progress-pill">
                  {task.progress}/{task.target}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <AlertModal
        open={Boolean(alert)}
        {...(alert || {})}
        onClose={closeAlert}
        onConfirm={alert?.mode === 'confirm' ? confirmAlert : closeAlert}
      />
      <PassStickerBurst show={sticker} onDone={() => setSticker(false)} />
      <AlertModal
        open={showHow}
        icon="bulb.png"
        title={t('passHowTitle')}
        body={[t('passHowTap'), t('passHowTasks'), t('passHowDaily'), t('passHowPremium')].join('\n\n')}
        onClose={() => setShowHow(false)}
        confirmLabel={t('gotIt')}
      />
    </main>
  );
};

export default Tasks;
